import type { RiskIndicatorMetricResults } from '@api';
import { useApi } from '@data-access';
import { compareRiskIndicatorsBySettingsOrder } from '@helpers';
import { useRiskMetricComments } from '@hooks/useRiskMetricComments';
import { getColumns, UnweightedScoreRow } from '@sections/UnweightedValuesTable/_components/Columns/Columns';
import { settingsStore } from '@stores/settings.store';
import { useCallback, useMemo, useState } from 'react';

interface UseAssessmentSummaryTableParams {
  riskResults?: RiskIndicatorMetricResults;
  onCommentChange?: (riskMetricResultId: number, comment: string) => void;
  onMetricClick?: (metricName: string) => void;
}

export const useAssessmentSummaryTable = ({
  riskResults,
  onCommentChange,
  onMetricClick,
}: UseAssessmentSummaryTableParams) => {
  const { api } = useApi();
  const { riskLimits } = settingsStore;
  const [tableRows, setTableRows] = useState<UnweightedScoreRow[]>([]);

  useMemo(() => {
    if (!riskResults?.riskMetrics) {
      setTableRows([]);
      return;
    }

    const rows = riskResults.riskMetrics
      .map(item => ({
        riskIndicator: item.metric?.indicator?.name || '',
        metric: item.metric?.name || '',
        unweightedRisk: Number(item.value),
        comment: item.comment || '',
        riskMetricResultId: item.id,
        isApplicable: item.isApplicable,
        confidenceLow: item.confidenceLow,
        confidenceHigh: item.confidenceHigh,
      }))
      .sort((a, b) => {
        const indicatorCompare = compareRiskIndicatorsBySettingsOrder(a.riskIndicator, b.riskIndicator);
        if (indicatorCompare !== 0) return indicatorCompare;
        return a.metric.localeCompare(b.metric);
      });

    setTableRows(rows);
  }, [riskResults]);

  const updateRowComment = useCallback((row: UnweightedScoreRow, updatedComment: string) => {
    if (row.riskMetricResultId == null) {
      return;
    }

    setTableRows(prev =>
      prev.map(item =>
        item.riskMetricResultId === row.riskMetricResultId ? { ...item, comment: updatedComment } : item,
      ),
    );
  }, []);

  const comments = useRiskMetricComments({
    api,
    updateRowComment,
    onCommentChange,
  });

  const columns = useMemo(
    () =>
      getColumns({
        onEditComment: comments.openCommentModal,
        onDeleteComment: comments.deleteComment,
        onMetricClick,
        indicatorLimits: {
          performance: riskLimits?.performance,
          robustness: riskLimits?.robustness,
          fairness: riskLimits?.fairness,
          explainability: riskLimits?.explainability,
        },
      }),
    [comments.openCommentModal, comments.deleteComment, onMetricClick, riskLimits],
  );

  return {
    tableRows,
    setTableRows,
    columns,
    ...comments,
  };
};
